// ProductCheckoutPage.js
//
// Public, unauthenticated hosted checkout for a Blitz Commerce product link
// (routes.commerceCheckout = "/checkout/:slug", matching the checkout_url
// format documented in backend-t2a.md: https://.../checkout/{slug}). This
// is the "Cookie Dozen" URL a merchant drops into their WooCommerce "Buy"
// button.
//
// A product link is reusable across unlimited buyers, but the underlying
// payment rail (T2A / pay-request) is single-use per invoice. So this page
// does NOT reuse one pre-made T2A link — on submit it asks the backend to
// mint a brand new single-use T2A pay-request for this product's price,
// addressed to the buyer's email.
//
// IMPORTANT — this used to `navigate()` to a separate /pay-request/:token
// page (PayRequestPage.js) to collect payment, which meant two client-side
// hops before the buyer even got to Stripe/PayPal. That's gone: the rail
// picker (card vs PayPal) and fee breakdown now live right here, and on
// submit we mint the pay-request token AND immediately call
// balanceLedger.payRequestByToken with it in the same mutation — no
// intermediate route. The only redirect left is the one that's actually
// unavoidable: the final hop to Stripe's/PayPal's own hosted payment page
// (window.location.href = approval_url), since we don't embed Stripe
// Elements / the PayPal SDK inline here.
//
// Money lands in the specific GlobalUser (company user) who created the
// product, resolved via T2A's own mechanism — NOT CompanyModel.balance,
// which isn't used in this build (see the note at the top of
// ProductManagement.js). requestProductPayment must be a new public,
// service-authenticated endpoint, not a passthrough of the existing
// authenticated /gu-balance/request-transfer route — see the detailed
// EXPECTED API SHAPES note in ProductManagement.js for why.
//
// Backend endpoints referenced here (client.commerce.fetchPublicProduct,
// client.commerce.requestProductPayment) do not exist yet — see
// src/API/endpoints.js for the expected shapes. balanceLedger.payRequestByToken
// is the existing public T2A endpoint (POST /gu-balance/pay-request/:token)
// already used by PaymentCTA.js for the same rail-select-and-redirect flow.

import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box, Typography, Paper, Avatar, Stack, Skeleton, CircularProgress,
  TextField, Button, Alert, Divider,
} from "@mui/material";
import {
  Storefront as StorefrontIcon,
  Lock as LockIcon,
  ErrorOutline as ErrorIcon,
  CreditCard as CreditCardIcon,
  Payment as PayPalIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as RedirectIcon,
} from "@mui/icons-material";
import { alpha, keyframes } from "@mui/material/styles";
import { useQuery, useMutation } from "react-query";
import client from "../../API";
import { isValidEmail } from "../BlitzPay/t2a/T2a.utils.js";
import { COMMERCE_BRAND as BLITZ, formatCurrency as fmt } from "./commerceShared.js";

const fadeUp = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;

// Display estimate only, so the buyer isn't surprised by the total before
// redirecting — the server recomputes the authoritative charge (see
// backend-t2a.md: "Server-side amount recomputation"). Same rates PaymentCTA
// uses for the equivalent T2A card/PayPal rail.
const RAILS = {
  stripe: { pct: 0.035, fixed: 0.30, label: "Card / Bank", icon: <CreditCardIcon sx={{ fontSize: 16 }} /> },
  paypal: { pct: 0.029, fixed: 0.30, label: "PayPal", icon: <PayPalIcon sx={{ fontSize: 16 }} /> },
};
function estimateTotal(price, rail) {
  const r = RAILS[rail] || RAILS.stripe;
  const gross = (price + r.fixed) / (1 - r.pct);
  return { total: +gross.toFixed(2), fee: +(gross - price).toFixed(2) };
}

function PageShell({ children }) {
  return (
    <Box sx={{
      minHeight: "100vh", position: "relative", overflow: "hidden",
      background: `linear-gradient(160deg, ${BLITZ.skyDeep} 0%, ${BLITZ.sky} 55%, ${alpha(BLITZ.skyLight, 0.9)} 100%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      py: { xs: 4, md: 8 }, px: 2,
    }}>
      <Box sx={{
        position: "absolute", inset: 0, opacity: 0.06, pointerEvents: "none",
        backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
        backgroundSize: "32px 32px",
      }} />
      <Box sx={{ width: "100%", maxWidth: 440, position: "relative", zIndex: 1, animation: `${fadeIn} 0.3s ease` }}>
        {children}
      </Box>
    </Box>
  );
}

// Shows the merchant's own company name/logo (resolved server-side from
// their company profile and returned on fetchPublicProduct as
// company_name/company_logo_url) instead of generic "Blitz Commerce"
// branding, so a buyer sees who they're actually paying. Falls back to
// the Blitz mark while loading or if a product has no company info yet.
function BrandMark({ companyName, companyLogoUrl }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 3 }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: 1.5, bgcolor: alpha("#fff", 0.18),
        display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(6px)",
        border: `1px solid ${alpha("#fff", 0.3)}`, overflow: "hidden",
      }}>
        {companyLogoUrl ? (
          <Box component="img" src={companyLogoUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <StorefrontIcon sx={{ fontSize: 16, color: "white" }} />
        )}
      </Box>
      <Typography variant="subtitle2" sx={{ color: "white", fontWeight: 800, letterSpacing: "0.02em" }}>
        {companyName || "Blitz Commerce"}
      </Typography>
    </Stack>
  );
}

function LoadingState() {
  return (
    <PageShell>
      <BrandMark />
      <Paper elevation={0} sx={{ borderRadius: 4, p: 4, textAlign: "center", animation: `${fadeUp} 0.25s ease` }}>
        <CircularProgress size={32} thickness={4} sx={{ color: BLITZ.sky, mb: 2 }} />
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          Loading product…
        </Typography>
        <Stack spacing={1} sx={{ mt: 3 }}>
          <Skeleton variant="rounded" height={14} width="60%" sx={{ mx: "auto" }} />
          <Skeleton variant="rounded" height={36} width="40%" sx={{ mx: "auto" }} />
        </Stack>
      </Paper>
    </PageShell>
  );
}

function ProblemState({ message }) {
  return (
    <PageShell>
      <BrandMark />
      <Paper elevation={0} sx={{ borderRadius: 4, p: 4, textAlign: "center", animation: `${fadeUp} 0.25s ease` }}>
        <Box sx={{
          width: 72, height: 72, borderRadius: "50%", bgcolor: "#fef2f2", color: "#dc2626",
          display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2.5,
        }}>
          <ErrorIcon sx={{ fontSize: 40 }} />
        </Box>
        <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
          Product not available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {message || "This product link is no longer active. Double-check the URL you were sent."}
        </Typography>
      </Paper>
    </PageShell>
  );
}

/*------------------------------------------------------------------
  Payment rail toggle — same two rails PaymentCTA offers, restyled for
  this page's light card instead of PaymentCTA's dark gradient card.
-------------------------------------------------------------------*/
function RailOption({ id, selected, onSelect }) {
  const r = RAILS[id];
  return (
    <Box
      onClick={() => onSelect(id)}
      sx={{
        flex: 1, py: 1.25, px: 1, borderRadius: 2, cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75,
        border: "1.5px solid",
        borderColor: selected ? BLITZ.sky : "divider",
        bgcolor: selected ? alpha(BLITZ.sky, 0.08) : "transparent",
        transition: "all 0.15s ease",
      }}
    >
      <Box sx={{ color: selected ? BLITZ.skyDark : "text.secondary", display: "flex" }}>{r.icon}</Box>
      <Typography variant="body2" fontWeight={700} sx={{ color: selected ? BLITZ.skyDark : "text.secondary" }}>
        {r.label}
      </Typography>
      {selected && <CheckCircleIcon sx={{ fontSize: 14, color: BLITZ.sky }} />}
    </Box>
  );
}

export default function ProductCheckoutPage() {
  const { slug } = useParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [rail, setRail] = useState("stripe");
  const [activeImage, setActiveImage] = useState(null);
  // Captured once, at the moment the buyer lands on this page — this is
  // whatever site linked them here (e.g. the merchant's WooCommerce store),
  // used as a fallback "return to site" target on the success page if the
  // merchant didn't set an explicit return_url. Browsers often strip this
  // for cross-origin/privacy reasons, so it's a nice-to-have, not reliable.
  const [initialReferrer] = useState(() => document.referrer || null);

  const { data: product, isLoading, isError, error } = useQuery(
    ["commerce", "public-product", slug],
    () => client.commerce.fetchPublicProduct(slug),
    { retry: false },
  );

  // One mutation, one redirect: mint the single-use T2A pay-request, then
  // immediately pay against it — the buyer never sees an intermediate
  // /pay-request/:token screen, they just land on Stripe/PayPal. Right
  // before that redirect, stash this checkout's context (company name,
  // return_url) in sessionStorage keyed by the pay-request token, so
  // PayRequestSuccessPage.js — the actual page Stripe/PayPal sends the
  // buyer back to — can read it after the round trip and either bounce
  // them back to the merchant's site or show a branded thank-you screen.
  const payMutation = useMutation(
    async () => {
      const { token } = await client.commerce.requestProductPayment(slug, {
        buyer_name: name.trim(),
        buyer_email: email.trim(),
      });
      if (!token) throw new Error("Couldn't start checkout — try again.");
      const payResponse = await client.balanceLedger.payRequestByToken(token, {
        rail,
        payer_name: name.trim(),
        payer_email: email.trim(),
      });
      return { ...payResponse, _checkoutToken: token };
    },
    {
      onSuccess: (data) => {
        const url = data?.approval_url || data?.url;
        if (!url) return;
        try {
          sessionStorage.setItem(
            `commerce_checkout_${data._checkoutToken}`,
            JSON.stringify({
              company_name: product?.company_name || null,
              product_name: product?.name || null,
              return_url: product?.return_url || null,
              referrer: initialReferrer,
            }),
          );
        } catch {
          // sessionStorage unavailable (private browsing, etc.) — non-fatal,
          // the success page just falls back to its generic confirmation.
        }
        window.location.href = url;
      },
    },
  );

  if (isLoading) return <LoadingState />;
  if (isError || !product) {
    return (
      <ProblemState message={error?.response?.data?.error || error?.message} />
    );
  }
  if (product.active === false) {
    return <ProblemState message="This product is currently paused by the seller." />;
  }

  const nameValid = name.trim().length > 0;
  const emailValid = isValidEmail(email);
  const { total, fee } = estimateTotal(parseFloat(product.price) || 0, rail);
  const railLabel = RAILS[rail].label;
  const galleryImages = Array.isArray(product.gallery_image_urls) ? product.gallery_image_urls : [];
  const heroImage = activeImage || product.image_url;

  return (
    <PageShell>
      <BrandMark companyName={product.company_name} companyLogoUrl={product.company_logo_url} />

      <Paper elevation={0} sx={{
        borderRadius: 4, p: { xs: 3, md: 3.5 }, mb: 2, position: "relative", overflow: "hidden",
        animation: `${fadeUp} 0.25s ease`,
      }}>
        <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 4,
          background: `linear-gradient(90deg, ${BLITZ.skyDeep}, ${BLITZ.sky})` }} />

        <Stack direction="row" alignItems="flex-start" spacing={1.75} sx={{ mb: 2 }}>
          <Avatar
            variant="rounded"
            src={heroImage || undefined}
            sx={{ width: 56, height: 56, bgcolor: alpha(BLITZ.sky, 0.12), color: BLITZ.skyDark }}
          >
            <StorefrontIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800}>
              {product.name}
            </Typography>
            {product.description && (
              <Typography variant="body2" color="text.secondary">
                {product.description}
              </Typography>
            )}
          </Box>
        </Stack>

        {galleryImages.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {[product.image_url, ...galleryImages].filter(Boolean).map((url, i) => (
              <Box
                key={url + i}
                onClick={() => setActiveImage(url)}
                component="img"
                src={url}
                alt=""
                sx={{
                  width: 40, height: 40, borderRadius: 1.5, objectFit: "cover", cursor: "pointer",
                  border: "2px solid",
                  borderColor: (heroImage || product.image_url) === url ? BLITZ.sky : "transparent",
                  opacity: (heroImage || product.image_url) === url ? 1 : 0.7,
                }}
              />
            ))}
          </Stack>
        )}

        <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: "-0.03em", color: BLITZ.ink, lineHeight: 1 }}>
          {fmt(product.price)}
        </Typography>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 4, p: { xs: 3, md: 3.5 }, animation: `${fadeUp} 0.3s ease` }}>
        {payMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {payMutation.error?.response?.data?.error ||
              payMutation.error?.message ||
              "Couldn't start checkout — try again."}
          </Alert>
        )}

        <TextField
          label="Name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <TextField
          label="Email"
          type="email"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ mb: 2.5 }}
        />

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.68rem", display: "block", mb: 1 }}
        >
          Payment method
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <RailOption id="stripe" selected={rail === "stripe"} onSelect={setRail} />
          <RailOption id="paypal" selected={rail === "paypal"} onSelect={setRail} />
        </Stack>

        <Box sx={{ borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.action.hover, 0.6), p: 1.75, mb: 2.5 }}>
          <Stack spacing={0.6}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">Item price</Typography>
              <Typography variant="caption" fontWeight={600}>{fmt(product.price)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">Processing fee (est.)</Typography>
              <Typography variant="caption" fontWeight={600}>+{fmt(fee)}</Typography>
            </Stack>
            <Divider sx={{ my: 0.5 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" fontWeight={700}>Total</Typography>
              <Typography variant="body1" fontWeight={800}>{fmt(total)}</Typography>
            </Stack>
          </Stack>
        </Box>

        <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={!nameValid || !emailValid || payMutation.isLoading}
          onClick={() => payMutation.mutate()}
          endIcon={payMutation.isLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <RedirectIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: "none" }}
        >
          {payMutation.isLoading ? `Redirecting to ${railLabel}…` : `Pay ${fmt(total)} via ${railLabel}`}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 1.25 }}>
          You'll complete payment securely on {railLabel}, then see a confirmation.
        </Typography>
      </Paper>

      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} sx={{ mt: 2.5, opacity: 0.85 }}>
        <LockIcon sx={{ fontSize: 13, color: "white" }} />
        <Typography variant="caption" sx={{ color: "white", fontSize: 11 }}>
          Secured by Blitz · Stripe · PayPal
        </Typography>
      </Stack>
    </PageShell>
  );
}
